import { useState } from "react";
import { toast } from "sonner";
import { Check, Info, Heart, Copy, RefreshCw } from "lucide-react";
import { apiFetch } from "@/api";
import { Button, Field } from "@/ui";

const BENEFITS = [
  "One MAXx across Everyday, Learn, Create and Research",
  "Voice, images, files and live research with sources",
  "Your chats and memory, kept for you",
];

export default function Paywall() {
  const [coupon, setCoupon] = useState("");
  const [loading, setLoading] = useState(false);
  const [friendLink, setFriendLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);

  const subscribe = async () => {
    setLoading(true);
    try {
      const d = await apiFetch("/payments/checkout", {
        method: "POST",
        body: { lookup_key: "max_monthly", origin_url: window.location.origin, coupon_code: coupon.trim() || undefined },
      });
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      toast("Couldn't start checkout. Please try again.");
    } catch (e) {
      toast(e.message);
    } finally {
      setLoading(false);
    }
  };

  const askFriend = async () => {
    setLinkLoading(true);
    try {
      const d = await apiFetch("/payments/sponsor-link", { method: "POST" });
      const link = `${window.location.origin}/pay-for-friend/${d.code}`;
      setFriendLink(link);
      try { await navigator.clipboard?.writeText(link); toast("Link copied — send it to a friend."); } catch { toast("Link ready — copy it below."); }
    } catch (e) { toast(e.message); } finally { setLinkLoading(false); }
  };

  const refreshLink = async () => {
    setLinkLoading(true);
    try {
      const d = await apiFetch("/payments/sponsor-link/rotate", { method: "POST" });
      const link = `${window.location.origin}/pay-for-friend/${d.code}`;
      setFriendLink(link);
      try { await navigator.clipboard?.writeText(link); } catch {}
      toast("New link ready — the old one no longer works.");
    } catch (e) { toast(e.message); } finally { setLinkLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-bold tracking-tight">Continue MAXx for $20/month.</h1>
          <p className="text-lg text-muted-foreground">
            Your 30-day free trial has ended. Your chats and account are safe — subscribe to keep going.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {BENEFITS.map((b, i) => (
            <div key={i} className="flex items-center gap-3">
              <Check className="h-5 w-5" strokeWidth={1.5} />
              <span>{b}</span>
            </div>
          ))}
        </div>
        <Field label="Coupon code (optional)" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="e.g. MAX20" data-testid="coupon-input" />
        <div className="flex gap-3 rounded-xl border border-border p-3 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" />
          <span>Secure checkout by Stripe. In test mode, use card <span className="font-semibold">4242 4242 4242 4242</span>, any future date and any CVC.</span>
        </div>
        <Button data-testid="subscribe-btn" onClick={subscribe} loading={loading}>Continue MAXx for $20/month</Button>

        <div className="flex flex-col gap-3 border-t border-border pt-6">
          <div className="flex items-start gap-3 text-sm text-muted-foreground">
            <Heart className="h-4 w-4 shrink-0" />
            <span>Can't pay yourself? Ask a friend to cover it — they pay, and your account unlocks instantly.</span>
          </div>
          {friendLink ? (
            <>
              <button data-testid="friend-link" onClick={() => { navigator.clipboard?.writeText(friendLink); toast("Link copied."); }}
                className="flex items-center justify-between gap-2 rounded-xl border border-border px-4 py-3 text-left text-sm">
                <span className="truncate">{friendLink}</span><Copy className="h-4 w-4 shrink-0" />
              </button>
              <button data-testid="refresh-friend-link" onClick={refreshLink} disabled={linkLoading}
                className="flex items-center gap-1.5 self-start text-sm text-muted-foreground underline underline-offset-2">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh link (stops the old one)
              </button>
            </>
          ) : (
            <Button variant="secondary" data-testid="ask-friend-btn" onClick={askFriend} loading={linkLoading}>Get my "pay for me" link</Button>
          )}
        </div>
      </div>
    </div>
  );
}
